import { Resume } from 'packages/types'
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import EditorRow from 'builder/components/EditorRow'
import { Container } from 'builder/components/PersonalDetailsSection/styles'
import { useI18n } from 'builder/hooks/useI18n'
import { RichTextEditorInstanceWrapper } from 'builder/components/RichTextArea/types'
import { useUpdateSimpleField } from 'builder/hooks/useUpdateSimpleField'
import FocusManager from 'builder/services/FocusManager'
import { SectionNames, actions, selectors } from 'builder/modules/resumeEditor'
import {
  SectionBodyFoldableContainer,
  SectionHeader,
  SectionHeaderFoldableContainer,
  SectionHint,
  useFoldableSectionScrollFix,
} from 'builder/components/Section'
import SectionTitle from 'builder/components/SectionTitle'
import RichTextArea from 'builder/components/RichTextArea'
import { TunerSectionTooltips } from 'builder/components/Tuner'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { CommuteTime } from './CommuteTime'
import { Dependants } from './Dependants'
import { SpouseObligation } from './SpouseObligation'
import { Spouse } from './Spouse'

interface Props {
  resume: Resume
}

export const ProfessionalSummary = (props: Props) => {
  const containerRef = useRef(null)
  const { resume } = props
  const { i18n } = useI18n()
  const richEditor = useRef<RichTextEditorInstanceWrapper>(null)
  const { updateSimpleField } = useUpdateSimpleField()
  const value = resume.profile
  const customTitle = resume.sectionTitles.profile
  const isOpened = useTypedSelector(store => selectors.isSectionOpened(store, SectionNames.profile))
  const dispatch = useDispatch()

  useEffect(() => {
    FocusManager.subscribe(SectionNames.profile, () => {
      if (richEditor.current) richEditor.current.focusEditor()
    })
    return () => FocusManager.unsubscribe(SectionNames.profile)
  }, [])

  useFoldableSectionScrollFix({
    elementRef: containerRef,
    isOpened,
  })

  const handleDescriptionChange = useCallback(
    (value: string) => updateSimpleField({ name: 'profile', value, debounce: true }),
    [updateSimpleField],
  )

  const handleToggle = useCallback(() => {
    dispatch(actions.setOpenedSections({ [SectionNames.profile]: !isOpened }))
  }, [dispatch, isOpened])

  return (
    <>
      <Container ref={containerRef}>
        <SectionHeader isOpened={isOpened} onClick={handleToggle}>
          <SectionTitle
            title={i18n.t('builder.resume_editor.professional_summary.title')}
            customTitle={customTitle}
            isOpened={isOpened}
          />
          <SectionHeaderFoldableContainer isOpened={isOpened}>
            <SectionHint>{i18n.t('builder.resume_editor.professional_summary.label')}</SectionHint>
          </SectionHeaderFoldableContainer>
        </SectionHeader>

        <SectionBodyFoldableContainer isOpened={isOpened}>
          <EditorRow>
            <RichTextArea
              instanceRef={richEditor}
              placeholder={i18n.t('builder.resume_editor.professional_summary.placeholder')}
              value={value || ''}
              onChange={handleDescriptionChange}
              boldButtonDisable
              italicButtonDisable
              linkButtonDisable
            />
          </EditorRow>

          <TunerSectionTooltips sectionId={SectionNames.profile} />

          <EditorRow>
            <CommuteTime />
            <Dependants />
          </EditorRow>

          <EditorRow>
            <Spouse />
            <SpouseObligation />
          </EditorRow>
        </SectionBodyFoldableContainer>
      </Container>
    </>
  )
}
